import { InputNumber, Slider } from "antd";
import { useTranslation } from "react-i18next";
import useSettledNumber from "./useSettledNumber";

interface SlideInputProps
{
      label?: string;
      value: number;
      min?: number;
      max?: number;
      step?: number;
      onChange: (val: number) => void;
      disabled?: boolean;
}

/**
 * Single-value counterpart to RangeInput: a number box paired with a slider,
 * both views of the same value so dragging and typing stay in sync.
 *
 * The box is there because a slider on its own shows a handle position and no
 * number, and because a value is often quicker to type than to drag onto.
 */
const SlideInput = ({
      label,
      value,
      min = 0,
      max = 100,
      step = 1,
      onChange,
      disabled,
}: SlideInputProps) =>
{
      const { t } = useTranslation();

      const { draft, handleChange, handleBlur, clearAdjustment, hint, notice } =
            useSettledNumber({
                  value,
                  min,
                  max,
                  step,
                  // The handle can only stand on the grid, so a typed value has to
                  // settle onto it as well or the two would disagree.
                  snapToStep: true,
                  onChange,
            });

      return (
            <div className="w-full">
                  {label && <p className="text-sm mb-3 text-gray-300">{t(label)}</p>}

                  <div className="w-full flex justify-between gap-4 items-center">
                        <InputNumber
                              min={min}
                              max={max}
                              step={step}
                              value={draft}
                              className="div-dropdown !w-[68px] min-w-[68px]"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              onFocus={clearAdjustment}
                              disabled={disabled}
                        />

                        <Slider
                              className="w-full"
                              min={min}
                              max={max}
                              step={step}
                              value={typeof value === "number" ? value : min}
                              onChange={(val) => onChange(val as number)}
                              disabled={disabled}
                        />
                  </div>

                  <p
                        className={`text-xs mt-1 mb-0 ${notice ? "text-yellow-400" : "text-gray-500"
                              }`}
                        role={notice ? "status" : undefined}
                  >
                        {notice ?? hint}
                  </p>
            </div>
      );
};

export default SlideInput;
